/**
 * 文本生成链路的数据契约与 Mock 生成器（P2-T1）。
 *
 * 为什么单独成文件：文本产物（文学剧本 / 分镜表）是**结构化文档**，下游主体/图像/视频
 * 节点要按字段消费，不能塞进 GenerationParams（那套是给图/视频/TTS 的媒体生成参数）。
 * 这里只借 CanvasNode.payload（unknown）承载，用下面的类型守卫读写，canvas.ts 不受牵连。
 *
 * Mock 先行（用户 2026-10-01 决策）：generateXxxMock 是纯函数，给定提示词返回内置示例结构，
 * 先把「点生成 → 回填 → 下游读取」的整条交互与数据流跑通；接真实 LLM 时换成后端 provider，
 * 本文件的类型契约与 UI 一律不变。
 */
import { type AudioLine, type CanvasNode, type Vec2 } from './canvas';
import { buildKeyframePrompt, buildVideoPrompt, DEFAULT_NEGATIVE } from './prompts';

/** 文本生成能力：文学剧本创作 / 导演剧本拆分（两个独立节点，A 结构化 / B 两节点）。 */
export type TextGenCapability = 'textToScript' | 'scriptToStoryboard';

/* ---------- 文学剧本（结构化） ---------- */

export interface ScriptDialogue {
  character: string;
  line: string;
}

export interface ScriptScene {
  id: string;
  /** 场景标题：内/外 · 地点 · 日/夜 */
  heading: string;
  characters: string[];
  /** 动作 / 画面描写 */
  action: string;
  dialogues: ScriptDialogue[];
}

export interface ScriptEpisode {
  id: string;
  index: number;
  title: string;
  scenes: ScriptScene[];
}

export interface LiteraryScript {
  title: string;
  logline: string;
  episodes: ScriptEpisode[];
}

/* ---------- 分镜表（结构化） ---------- */

export interface StoryboardShot {
  id: string;
  index: number;
  scene: string;
  characters: string[];
  /** 出场道具（供“生成资产卡”抽道具卡；无描写则为空，不生成） */
  props: string[];
  /** 场景空间描述（80-150 字，喂关键帧/视频的场景层） */
  scene_description: string;
  action: string;
  /** 出场角色的当前造型视觉刻画（每人 80-150 字） */
  character_visual: string;
  /** 景别：远 / 全 / 中 / 近 / 特 */
  shotSize: string;
  /** 机位角度：平视/俯拍/仰拍/过肩/POV——行业把“角度”与“景别/运镜”分列 */
  cameraAngle: string;
  /** 运镜走势：推/拉/摇/移/跟/升降/固定/手持 + 轨迹 */
  cameraMove: string;
  /** 光影：光源/方向/色温/阴影/质感 */
  lighting: string;
  /** 色彩基调：主/辅/强调色 + 情绪 */
  colorTone: string;
  /** 情绪氛围 */
  emotion: string;
  /** 环境音效 + BGM 风格（50-100 字） */
  sound_design: string;
  /** 台词音色指导（语调/语速/情感，30-60 字） */
  voice_direction: string;
  /** 转场：硬切/淡入淡出/匹配剪辑 */
  transition: string;
  durationSec: number;
  dialogue: string;
}

export interface Storyboard {
  title: string;
  shots: StoryboardShot[];
}

/** 生成产物：剧本或分镜。回填进节点 payload.result。 */
export type GeneratedPayload =
  | { type: 'literaryScript'; script: LiteraryScript }
  | { type: 'storyboard'; storyboard: Storyboard };

/**
 * 文本生成节点的 payload 形状（存进 CanvasNode.payload）。
 * prompt 与 capability 常驻，result 在生成后回填；generating 这类瞬时态放 UI，不落盘。
 */
export interface TextGenPayload {
  type: 'textgen';
  capability: TextGenCapability;
  prompt: string;
  result?: GeneratedPayload | undefined;
}

/* ---------- 类型守卫（读 unknown payload 的唯一安全入口） ---------- */

function hasString(v: unknown, key: string): boolean {
  return typeof v === 'object' && v !== null && typeof (v as Record<string, unknown>)[key] === 'string';
}

export function isTextGenPayload(p: unknown): p is TextGenPayload {
  return (
    typeof p === 'object' &&
    p !== null &&
    (p as { type?: unknown }).type === 'textgen' &&
    hasString(p, 'capability') &&
    hasString(p, 'prompt')
  );
}

export function isLiteraryScriptResult(r: GeneratedPayload | undefined): r is { type: 'literaryScript'; script: LiteraryScript } {
  return r !== undefined && r.type === 'literaryScript';
}

export function isStoryboardResult(r: GeneratedPayload | undefined): r is { type: 'storyboard'; storyboard: Storyboard } {
  return r !== undefined && r.type === 'storyboard';
}

/* ---------- 统计与预览（节点卡展示用） ---------- */

export function scriptSceneCount(s: LiteraryScript): number {
  return s.episodes.reduce((sum, ep) => sum + ep.scenes.length, 0);
}

export function storyboardShotCount(sb: Storyboard): number {
  return sb.shots.length;
}

/** 一行摘要，供节点卡副标题；无结果返回 undefined。 */
export function previewOfPayload(p: unknown): string | undefined {
  if (isShotPayload(p)) {
    return shotPreview(p);
  }
  if (!isTextGenPayload(p) || p.result === undefined) {
    return undefined;
  }
  if (isLiteraryScriptResult(p.result)) {
    return `剧本 · ${p.result.script.episodes.length} 集 / ${scriptSceneCount(p.result.script)} 场`;
  }
  if (isStoryboardResult(p.result)) {
    return `分镜 · ${storyboardShotCount(p.result.storyboard)} 镜`;
  }
  return undefined;
}

/**
 * 从上游产物组装图像提示词（“从上游生成”，§11.4 镜头语义注入）：
 * 分镜→首镜镜头语言拼合；剧本→标题+梗概+首场；无产物→回退传入文本。纯函数可测。
 */
export function composeImagePrompt(result: GeneratedPayload | undefined, fallbackText: string): string {
  if (isStoryboardResult(result)) {
    const sb = result.storyboard;
    const shot = sb.shots[0];
    if (shot !== undefined) {
      return [
        sb.title,
        shot.scene,
        shot.action,
        `景别${shot.shotSize}`,
        `机位${shot.cameraAngle}`,
        `运镜${shot.cameraMove}`,
        shot.lighting,
        shot.colorTone,
        shot.emotion,
      ]
        .filter((s) => s !== '' && s !== undefined)
        .join('，');
    }
    return sb.title;
  }
  if (isLiteraryScriptResult(result)) {
    const sc = result.script;
    const firstScene = sc.episodes[0]?.scenes[0];
    return [sc.title, sc.logline, firstScene?.heading, firstScene?.action]
      .filter((s): s is string => typeof s === 'string' && s !== '')
      .join('。');
  }
  return fallbackText.trim();
}

/** 默认负向提示词：生成正向 prompt 时同步自动填入（通用伪影层，见规范 §二）。 */
export function defaultNegativePrompt(): string {
  return DEFAULT_NEGATIVE;
}

/**
 * 从**单个分镜**组装关键帧 prompt—委托 prompts.buildKeyframePrompt（分层+负向+长度护栏）。
 */
export function composeImagePromptFromShot(shot: StoryboardShot): string {
  return buildKeyframePrompt(shot);
}

/** 分镜视频 prompt 拼装上下文（后续接真时从连线资产/配音填充）。 */
export interface VideoPromptCtx {
  /** 出场角色的参考图编号名（如 ['图1','图2']），供“图N”一致性引用 */
  characterRefs?: string[] | undefined;
  /** 是否已有首帧关键帧（图生视频） */
  hasKeyframe?: boolean | undefined;
}

/**
 * 从**单个分镜**组装分镜视频 prompt—委托 prompts.buildVideoPrompt（旧版 8 层+护栏）。
 * 当前为分层实现，接真实 LLM 时逐层深化（硬约束已存跨会话记忆：不得过简）。
 */
export function composeVideoPrompt(shot: StoryboardShot, ctx: VideoPromptCtx = {}): string {
  return buildVideoPrompt(shot, ctx);
}

/* ---------- Mock 生成器（纯函数，接真 LLM 前跑通用） ---------- */

/** 由提示词造一段可展示的标题：取前 12 字，空则回退。 */
function titleFrom(prompt: string, fallback: string): string {
  const t = prompt.trim();
  return t === '' ? fallback : t.slice(0, 12);
}

/** 文学剧本 Mock：回显提示词到 logline，产出一集两场带对白的示例结构。 */
export function mockLiteraryScript(prompt: string): LiteraryScript {
  const topic = prompt.trim() || '未命名故事';
  return {
    title: titleFrom(prompt, '未命名剧本'),
    logline: `（Mock）基于「${topic}」：主角在一次意外中被迫做出选择，命运由此转向。`,
    episodes: [
      {
        id: 'ep1',
        index: 1,
        title: '第1集 · 开端',
        scenes: [
          {
            id: 'sc1',
            heading: '内 · 出租屋 · 夜',
            characters: ['林晚'],
            action: '林晚盯着手机屏幕，指尖发颤。窗外雷雨。',
            dialogues: [{ character: '林晚', line: '这不可能……他怎么会现在出现。' }],
          },
          {
            id: 'sc2',
            heading: '外 · 楼下街道 · 夜',
            characters: ['林晚', '陈默'],
            action: '雨幕里，一辆车停在路灯下。陈默撑伞走来。',
            dialogues: [
              { character: '陈默', line: '上车，我送你。' },
              { character: '林晚', line: '我不用你送。' },
            ],
          },
        ],
      },
    ],
  };
}

/** 分镜 Mock：给定上游剧本则沿用其标题，产出 4 镜示例分镜表（字段对齐旧版+行业 12 项）。 */
export function mockStoryboard(prompt: string, source?: LiteraryScript): Storyboard {
  const title = source?.title ?? titleFrom(prompt, '分镜草稿');
  const shots: StoryboardShot[] = [
    { id: 'sh1', index: 1, scene: '出租屋·夜', characters: ['林晚'], props: ['手机'], scene_description: '狭小出租屋，墙上贴满旧海报，书桌堆着文件，台灯昏黄', action: '手机屏幕特写，林晚指尖发颤，瞳孔骤缩', character_visual: '林晚：黑色长发散乱，米白家居服，面色苍白，眼下青影', shotSize: '特写', cameraAngle: '平视', cameraMove: '固定', lighting: '手机冷光映脸，低照度', colorTone: '冷蓝主调', emotion: '紧张、压抑', sound_design: '窗外雷雨闷响，手机震动嗡嗡，低频紧张弦乐', voice_direction: '语速慢，气声，压抑的颤抖', transition: '硬切', durationSec: 3, dialogue: '林晚（独白）：这不可能……他怎么会现在出现。' },
    { id: 'sh2', index: 2, scene: '出租屋·夜', characters: ['林晚'], props: [], scene_description: '同上，窗边雷雨闪电', action: '林晚猛地站起，椅子后滑倒地，望向窗外', character_visual: '林晚：起身带风，衣摆扬起，指节攥白', shotSize: '中景', cameraAngle: '微仰', cameraMove: '轻推', lighting: '窗外雷光间歇打亮', colorTone: '冷灰 + 瞬暖', emotion: '不安', sound_design: '椅倒闷响，雨打玻璃，心跳声渐强', voice_direction: '短促、气息不稳', transition: '硬切', durationSec: 4, dialogue: '林晚：不……我不能再见他。' },
    { id: 'sh3', index: 3, scene: '楼下街道·夜', characters: ['陈默'], props: ['车'], scene_description: '湿漉街道，路灯拉出长光晕，车停在积水中', action: '车停在路灯下，雨丝斜织，陈默握方向盘指节发白', character_visual: '陈默：黑色短发背头，深灰西装湿肩，眼神坚定', shotSize: '全景', cameraAngle: '高角度俯拍', cameraMove: '横移', lighting: '路灯单点暖光，雨丝反光', colorTone: '暗夜暖黄点缀', emotion: '孤寂', sound_design: '雨落车顶，雨刷节拍，远处雷鸣', voice_direction: '低沉平稳，克制的等待', transition: '叠化', durationSec: 5, dialogue: '陈默（自语）：等她下来……今晚必须说清楚。' },
    { id: 'sh4', index: 4, scene: '楼下街道·夜', characters: ['林晚', '陈默'], props: ['伞'], scene_description: '车旁伞下狭小空间，街灯轮廓光', action: '陈默撑伞走近，两人对视，雨顺伞沿滑落', character_visual: '林晚：风衣湿袖，抬眼欲言又止；陈默：西装笔挺，目光灼灼', shotSize: '近景', cameraAngle: '过肩', cameraMove: '跟拍', lighting: '伞下阴影 + 街灯轮廓光', colorTone: '冷暖对比', emotion: '克制的情感张力', sound_design: '雨声包围，两人之间沉默的呼吸，大提琴渐入', voice_direction: '陈默低沉温柔，林晚冷硬带颤', transition: '硬切', durationSec: 6, dialogue: '陈默：上车，我送你。\n林晚：我不用你送。' },
  ];
  return { title, shots };
}

/**
 * 造一个文本生成节点：文学剧本→kind text，导演剧本→kind timeline；均 mode generator，
 * payload 携 capability+prompt（result 待回填）。generator 靠 payload 过校验（见 validateCanvasDoc）。
 * 导演剧本正常由文学剧本「拆分成导演剧本」自动派生，不手动建（流程修正）。
 */
export function createTextGenNode(capability: TextGenCapability, id: string, position: Vec2): CanvasNode {
  const isScript = capability === 'textToScript';
  const payload: TextGenPayload = { type: 'textgen', capability, prompt: '' };
  return {
    id,
    kind: isScript ? 'text' : 'timeline',
    mode: 'generator',
    title: isScript ? '文学剧本' : '导演剧本',
    position,
    payload,
  };
}

/* ---------- 分镜节点（刀A：把导演剧本总表物化成逐镜卡，下游连具体某镜） ---------- */

/** 单个分镜节点的 payload：携该镜完整结构化字段 + 溯源。 */
export interface ShotPayload {
  type: 'shot';
  shot: StoryboardShot;
  /** 第几集（1 基）；单集填 1 */
  episodeIndex: number;
  /** 来源导演剧本节点 id（供溯源） */
  sourceId?: string | undefined;
}

export function isShotPayload(p: unknown): p is ShotPayload {
  return (
    typeof p === 'object' &&
    p !== null &&
    (p as { type?: unknown }).type === 'shot' &&
    typeof (p as { shot?: unknown }).shot === 'object'
  );
}

/** 分镜卡统一标签：第E集·第S镜（用户要求卡名自带集/镜序号，便于一眼定位）。 */
export function shotLabel(episodeIndex: number, shotIndex: number): string {
  return `第${episodeIndex}集·第${shotIndex}镜`;
}

/** 造一个分镜节点：kind shot、mode generator（靠 payload 过校验）、标题=第E集·第S镜。 */
export function createShotNode(
  shot: StoryboardShot,
  id: string,
  position: Vec2,
  episodeIndex = 1,
  sourceId?: string,
): CanvasNode {
  const payload: ShotPayload = { type: 'shot', shot, episodeIndex, sourceId };
  return { id, kind: 'shot', mode: 'generator', title: shotLabel(episodeIndex, shot.index), position, payload };
}

/** 改分镜节点里当前镜的字段（不可变，返回新 payload）。 */
export function patchShot(p: ShotPayload, patch: Partial<Omit<StoryboardShot, 'id' | 'index'>>): ShotPayload {
  return { ...p, shot: { ...p.shot, ...patch } };
}

/** 分镜节点卡面一行摘要：场景 + 动作前段 + 景别·运镜。 */
export function shotPreview(p: ShotPayload): string {
  const bits = [p.shot.shotSize, p.shot.cameraMove].filter((s) => s !== '');
  const action = p.shot.action.length > 16 ? `${p.shot.action.slice(0, 16)}…` : p.shot.action;
  const tail = [action, bits.join('·')].filter((s) => s !== '').join(' ');
  return p.shot.scene !== '' ? `${p.shot.scene}｜${tail}` : tail;
}

/* ---------- 结构化产物的不可变编辑（P2-T2，纯函数可测） ---------- */

/**
 * 把一镜的台词拆成逐句配音行（多人对话逐行）：
 * 每行“角色：台词”→{role,text}；无前缀则归首个出场角色（无则旁白）；情绪默认中性。
 * 行内括号注释（如“陈默（自语）”）剔离以便匹配角色卡。无台词→空数组。
 */
export function parseDialogueLines(shot: StoryboardShot): AudioLine[] {
  const fallback = shot.characters[0]?.trim() ?? '旁白';
  const rawLines = shot.dialogue.split(/\n+/).map((s) => s.trim()).filter((s) => s !== '');
  return rawLines.map((line) => {
    const m = /^([^:：]{1,12})[:：]\s*(.+)$/.exec(line);
    if (m?.[1] !== undefined && m[2] !== undefined) {
      const role = m[1].replace(/[（(].*$/, '').trim() || fallback;
      return { role, text: m[2].trim(), emotion: '中性' };
    }
    return { role: fallback, text: line, emotion: '中性' };
  });
}

export function updateScriptMeta(
  s: LiteraryScript,
  patch: Partial<Pick<LiteraryScript, 'title' | 'logline'>>,
): LiteraryScript {
  return { ...s, ...patch };
}

export function updateScene(
  s: LiteraryScript,
  sceneId: string,
  patch: Partial<Pick<ScriptScene, 'heading' | 'action'>>,
): LiteraryScript {
  return {
    ...s,
    episodes: s.episodes.map((ep) => ({
      ...ep,
      scenes: ep.scenes.map((sc) => (sc.id === sceneId ? { ...sc, ...patch } : sc)),
    })),
  };
}

export function updateDialogue(
  s: LiteraryScript,
  sceneId: string,
  index: number,
  line: string,
): LiteraryScript {
  return {
    ...s,
    episodes: s.episodes.map((ep) => ({
      ...ep,
      scenes: ep.scenes.map((sc) =>
        sc.id !== sceneId
          ? sc
          : { ...sc, dialogues: sc.dialogues.map((d, i) => (i === index ? { ...d, line } : d)) },
      ),
    })),
  };
}

function reindex(shots: StoryboardShot[]): StoryboardShot[] {
  return shots.map((sh, i) => ({ ...sh, index: i + 1 }));
}

export function updateShot(
  sb: Storyboard,
  shotId: string,
  patch: Partial<Omit<StoryboardShot, 'id' | 'index'>>,
): Storyboard {
  return { ...sb, shots: sb.shots.map((sh) => (sh.id === shotId ? { ...sh, ...patch } : sh)) };
}

/** 在 afterId 之后（缺省末尾）插入一空镜，并重排 index */
export function addShot(sb: Storyboard, newId: string, afterId?: string): Storyboard {
  const blank: StoryboardShot = {
    id: newId,
    index: 0,
    scene: '',
    characters: [],
    props: [],
    scene_description: '',
    action: '',
    character_visual: '',
    shotSize: '中景',
    cameraAngle: '平视',
    cameraMove: '固定',
    lighting: '',
    colorTone: '',
    emotion: '',
    sound_design: '',
    voice_direction: '',
    transition: '硬切',
    durationSec: 3,
    dialogue: '',
  };
  const at = afterId === undefined ? sb.shots.length : sb.shots.findIndex((s) => s.id === afterId) + 1;
  const shots = [...sb.shots];
  shots.splice(at < 0 ? sb.shots.length : at, 0, blank);
  return { ...sb, shots: reindex(shots) };
}

export function removeShot(sb: Storyboard, shotId: string): Storyboard {
  return { ...sb, shots: reindex(sb.shots.filter((s) => s.id !== shotId)) };
}

/** 上移(-1)/下移(+1)一镜；越界不动 */
export function moveShot(sb: Storyboard, shotId: string, dir: -1 | 1): Storyboard {
  const i = sb.shots.findIndex((s) => s.id === shotId);
  const j = i + dir;
  if (i < 0 || j < 0 || j >= sb.shots.length) {
    return sb;
  }
  const shots = [...sb.shots];
  const a = shots[i];
  const b = shots[j];
  if (a === undefined || b === undefined) {
    return sb;
  }
  shots[i] = b;
  shots[j] = a;
  return { ...sb, shots: reindex(shots) };
}
